import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Play, CheckCircle, Users, Award, Heart, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';

const CustomerLandingPage = () => {
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const navigate = useNavigate();

    // Sample portfolio images - replace with your actual images
    const portfolioImages = [
        "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2058&q=80",
        "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
        "https://images.unsplash.com/photo-1554995207-c18c203602cb?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
        "https://images.unsplash.com/photo-1565814329452-e1efa11c5b89?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2043&q=80"
    ];

    // Auto-rotate portfolio images
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentImageIndex((prev) => (prev + 1) % portfolioImages.length);
        }, 4000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 text-white">
            {/* Hero Section */}
            <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
                {/* Background - Your Stunning Interior Photo */}
                <div className="absolute inset-0 z-0">
                    <img 
                        src="https://customer-assets.emergentagent.com/job_sleek-showcase-46/artifacts/pbaudki8_10-web-or-mls-014%203.JPG"
                        alt="Established Design Co. - Luxury Interior Design" 
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
                </div>

                {/* Hero Content */}
                <div className="relative z-10 text-center px-6 max-w-6xl mx-auto">
                    {/* Your Logo */}
                    <div className="mb-8">
                        <img
                            src="https://customer-assets.emergentagent.com/job_sleek-showcase-46/artifacts/c5c84fh5_Established%20logo.png"
                            alt="Established Design Co."
                            className="h-24 md:h-32 mx-auto object-contain"
                            style={{ filter: 'brightness(0) saturate(100%) invert(85%) sepia(15%) saturate(664%) hue-rotate(349deg) brightness(95%) contrast(88%)' }} // Converts to your brand gold color
                        />
                        <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-[#B49B7E] to-transparent mx-auto mt-6"></div>
                    </div>

                    {/* Tagline */}
                    <p className="text-xl md:text-2xl font-light mb-8 max-w-3xl mx-auto leading-relaxed" style={{ color: '#F5F5DC' }}>
                        Creating extraordinary spaces that reflect your unique story and elevate your everyday life
                    </p>

                    {/* Video Section */}
                    <div className="mb-12">
                        <div className="relative inline-block">
                            {!isVideoPlaying ? (
                                <div 
                                    className="relative cursor-pointer group"
                                    onClick={() => setIsVideoPlaying(true)}
                                >
                                    <div className="w-64 h-36 bg-black/50 rounded-2xl flex items-center justify-center backdrop-blur-sm border border-[#B49B7E]/30 group-hover:border-[#B49B7E]/60 transition-all duration-300">
                                        <Play className="w-12 h-12 text-[#B49B7E] group-hover:scale-110 transition-transform duration-300" />
                                    </div>
                                    <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-sm font-light" style={{ color: '#F5F5DC' }}>
                                        Meet Our Team
                                    </div>
                                </div>
                            ) : (
                                <div className="w-96 h-56 bg-black rounded-2xl overflow-hidden border border-[#B49B7E]/30">
                                    {/* Replace with your actual video */}
                                    <iframe
                                        src="https://player.vimeo.com/video/example"
                                        className="w-full h-full"
                                        allow="autoplay; fullscreen"
                                        allowFullScreen
                                    ></iframe>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* CTA Button */}
                    <Button 
                        onClick={() => navigate('/customer/questionnaire')}
                        className="bg-gradient-to-r from-[#B49B7E] to-[#A08B6F] hover:from-[#A08B6F] hover:to-[#8B7355] text-white px-12 py-4 text-lg font-medium rounded-full shadow-2xl hover:shadow-[#B49B7E]/25 transition-all duration-300 transform hover:scale-105"
                    >
                        Begin Your Design Journey
                        <ArrowRight className="ml-3 w-5 h-5" />
                    </Button>

                    {/* Scroll Indicator */}
                    <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
                        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
                            <div className="w-1 h-3 bg-white/50 rounded-full mt-2"></div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Who We Are Section */}
            <section className="py-20 px-6 bg-gradient-to-b from-black via-gray-900 to-black">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-5xl font-light text-[#B49B7E] mb-6">Who We Are</h2>
                        <div className="w-32 h-0.5 bg-gradient-to-r from-transparent via-[#B49B7E] to-transparent mx-auto mb-8"></div>
                        <p className="text-xl max-w-4xl mx-auto leading-relaxed" style={{ color: '#F5F5DC' }}>
                            We are passionate creators of extraordinary living spaces, dedicated to transforming your vision into reality with unparalleled attention to detail and timeless elegance.
                        </p>
                    </div>

                    {/* Team Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
                        {/* Philosophy */}
                        <div className="text-center group">
                            <div className="w-20 h-20 bg-gradient-to-br from-[#B49B7E] to-[#A08B6F] rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                                <Heart className="w-10 h-10 text-white" />
                            </div>
                            <h3 className="text-2xl font-light mb-4" style={{ color: '#F5F5DC' }}>Our Philosophy</h3>
                            <p className="leading-relaxed" style={{ color: '#F5F5DC', opacity: '0.8' }}>
                                Every space tells a story. We believe in creating environments that not only inspire but deeply connect with who you are and how you live.
                            </p>
                        </div>

                        {/* Expertise */}
                        <div className="text-center group">
                            <div className="w-20 h-20 bg-gradient-to-br from-[#B49B7E] to-[#A08B6F] rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                                <Award className="w-10 h-10 text-white" />
                            </div>
                            <h3 className="text-2xl font-light mb-4" style={{ color: '#F5F5DC' }}>Our Expertise</h3>
                            <p className="leading-relaxed" style={{ color: '#F5F5DC', opacity: '0.8' }}>
                                With years of experience in luxury design, we specialize in new builds, renovations, and complete styling transformations.
                            </p>
                        </div>

                        {/* Commitment */}
                        <div className="text-center group">
                            <div className="w-20 h-20 bg-gradient-to-br from-[#B49B7E] to-[#A08B6F] rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                                <Star className="w-10 h-10 text-white" />
                            </div>
                            <h3 className="text-2xl font-light mb-4" style={{ color: '#F5F5DC' }}>Our Commitment</h3>
                            <p className="leading-relaxed" style={{ color: '#F5F5DC', opacity: '0.8' }}>
                                From initial concept to final installation, we ensure every detail exceeds your expectations and creates lasting memories.
                            </p>
                        </div>
                    </div>

                    {/* Process Overview */}
                    <div className="bg-gradient-to-br from-black/80 to-gray-900/90 backdrop-blur-sm rounded-3xl p-12 border border-[#B49B7E]/20 shadow-2xl">
                        <h3 className="text-4xl font-light text-[#B49B7E] text-center mb-12">Our Design Process</h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                            {[
                                { step: "01", title: "Discovery", desc: "We start with your comprehensive questionnaire to understand your vision, lifestyle, and dreams." },
                                { step: "02", title: "Planning", desc: "Our team creates detailed plans and concepts tailored specifically to your unique needs." },
                                { step: "03", title: "Curation", desc: "We carefully select every piece, from furniture to finishes, ensuring perfect harmony." },
                                { step: "04", title: "Installation", desc: "We bring your vision to life with meticulous attention to every detail and timeline." }
                            ].map((item, index) => (
                                <div key={index} className="text-center">
                                    <div className="text-3xl font-light text-[#B49B7E] mb-4">{item.step}</div>
                                    <h4 className="text-xl font-medium mb-3" style={{ color: '#F5F5DC' }}>{item.title}</h4>
                                    <p className="text-sm leading-relaxed" style={{ color: '#F5F5DC', opacity: '0.8' }}>{item.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* All in the Details Showcase */}
            <section className="py-20 px-6 bg-gradient-to-b from-black via-gray-900 to-black">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-5xl font-light text-[#B49B7E] mb-6">All in the Details</h2>
                        <div className="w-32 h-0.5 bg-gradient-to-r from-transparent via-[#B49B7E] to-transparent mx-auto mb-8"></div>
                        <p className="text-xl max-w-3xl mx-auto" style={{ color: '#F5F5DC', opacity: '0.9' }}>
                            The thoughtful curation and styling that transforms houses into extraordinary homes
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Vintage Clock Collection */}
                        <div className="group cursor-pointer">
                            <div className="relative overflow-hidden rounded-2xl aspect-[4/3]">
                                <img 
                                    src="https://customer-assets.emergentagent.com/job_sleek-showcase-46/artifacts/lhwi6kpk_0FB9969A-C111-4078-A3FC-6711D2015941_1_105_c.jpeg"
                                    alt="Curated vintage clock collection"
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                <div className="absolute bottom-4 left-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                    <h4 className="text-lg font-medium" style={{ color: '#F5F5DC' }}>Curated Collections</h4>
                                    <p className="text-sm" style={{ color: '#F5F5DC', opacity: '0.8' }}>Thoughtful styling & vintage finds</p>
                                </div>
                            </div>
                        </div>

                        {/* Brass & Books Styling */}
                        <div className="group cursor-pointer">
                            <div className="relative overflow-hidden rounded-2xl aspect-[4/3]">
                                <img 
                                    src="https://customer-assets.emergentagent.com/job_sleek-showcase-46/artifacts/uw773jrc_17-IMG_2489.jpg"
                                    alt="Brass accessories and book styling"
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                <div className="absolute bottom-4 left-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                    <h4 className="text-lg font-medium" style={{ color: '#F5F5DC' }}>Artful Arrangements</h4>
                                    <p className="text-sm" style={{ color: '#F5F5DC', opacity: '0.8' }}>Brass accents & curated objects</p>
                                </div>
                            </div>
                        </div>

                        {/* Office Design with Dog */}
                        <div className="group cursor-pointer">
                            <div className="relative overflow-hidden rounded-2xl aspect-[4/3]">
                                <img 
                                    src="https://customer-assets.emergentagent.com/job_sleek-showcase-46/artifacts/gtmb5fh5_20-IMG_2441.jpg"
                                    alt="Custom home office design"
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                <div className="absolute bottom-4 left-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                    <h4 className="text-lg font-medium" style={{ color: '#F5F5DC' }}>Custom Built-Ins</h4>
                                    <p className="text-sm" style={{ color: '#F5F5DC', opacity: '0.8' }}>Functional luxury & pattern play</p>
                                </div>
                            </div>
                        </div>

                        {/* Luxury Bathroom */}
                        <div className="group cursor-pointer">
                            <div className="relative overflow-hidden rounded-2xl aspect-[4/3]">
                                <img 
                                    src="https://customer-assets.emergentagent.com/job_sleek-showcase-46/artifacts/unq2tzy0_5-IMG_2599.jpg"
                                    alt="Luxury bathroom with pattern tiles"
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                <div className="absolute bottom-4 left-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                    <h4 className="text-lg font-medium" style={{ color: '#F5F5DC' }}>Luxury Finishes</h4>
                                    <p className="text-sm" style={{ color: '#F5F5DC', opacity: '0.8' }}>Statement tiles & elegant fixtures</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Final CTA Section */}
            <section className="py-20 px-6 bg-gradient-to-b from-black via-gray-900 to-black">
                <div className="max-w-4xl mx-auto">
                    <div className="bg-gradient-to-br from-black/80 to-gray-900/90 backdrop-blur-sm rounded-3xl p-12 border border-[#B49B7E]/20 shadow-2xl text-center">
                        <h2 className="text-5xl font-light text-[#B49B7E] mb-8">Ready to Begin?</h2>
                        <p className="text-xl mb-12 leading-relaxed" style={{ color: '#F5F5DC', opacity: '0.9' }}>
                            Take the first step towards your dream space. Our comprehensive questionnaire helps us understand your unique vision and creates the foundation for an extraordinary design journey.
                        </p>
                        
                        <div className="flex flex-col items-center gap-6">
                            <Button 
                                onClick={() => navigate('/customer/questionnaire')}
                                className="bg-gradient-to-r from-[#B49B7E] to-[#A08B6F] hover:from-[#A08B6F] hover:to-[#8B7355] text-[#F5F5DC] px-16 py-6 text-xl font-medium rounded-full shadow-2xl hover:shadow-[#B49B7E]/25 transition-all duration-300 transform hover:scale-105"
                            >
                                Start Your Questionnaire
                                <ArrowRight className="ml-4 w-6 h-6" />
                            </Button>
                            
                            <div className="flex items-center gap-6 text-sm" style={{ color: '#F5F5DC', opacity: '0.7' }}>
                                <div className="flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4 text-[#B49B7E]" />
                                    <span>Takes 10-15 minutes</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4 text-[#B49B7E]" />
                                    <span>Completely confidential</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4 text-[#B49B7E]" />
                                    <span>No obligation consultation</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 px-6 bg-black">
                <div className="max-w-4xl mx-auto text-center">
                    <img
                        src="https://customer-assets.emergentagent.com/job_sleek-showcase-46/artifacts/c5c84fh5_Established%20logo.png"
                        alt="Established Design Co."
                        className="h-12 mx-auto object-contain mb-4"
                        style={{ filter: 'brightness(0) saturate(100%) invert(85%) sepia(15%) saturate(664%) hue-rotate(349deg) brightness(95%) contrast(88%)' }}
                    />
                    <p className="text-sm mb-6" style={{ color: '#F5F5DC', opacity: '0.7' }}>
                        Creating extraordinary spaces since 2020
                    </p>
                    <div className="flex justify-center gap-8 text-sm" style={{ color: '#F5F5DC', opacity: '0.5' }}>
                        <span>Privacy Policy</span>
                        <span>Terms of Service</span>
                        <span>Contact</span>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default CustomerLandingPage;